import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Container, Grid, Paper, Button } from '@mui/material';
import { motion } from 'framer-motion';

const countries = [
  { name: 'USA', flag: 'us', subtitle: 'North America' },
  { name: 'UK', flag: 'gb', subtitle: 'Europe' },
  { name: 'Germany', flag: 'de', subtitle: 'Europe' },
  { name: 'Australia', flag: 'au', subtitle: 'Oceania' },
  { name: 'UAE', flag: 'ae', subtitle: 'Middle East' },
  { name: 'India', flag: 'in', subtitle: 'Asia' },
  { name: 'France', flag: 'fr', subtitle: 'Europe' },
  { name: 'Japan', flag: 'jp', subtitle: 'Asia' },
  { name: 'Morocco', flag: 'ma', subtitle: 'Africa' },
  { name: 'Singapore', flag: 'sg', subtitle: 'Asia' },
  { name: 'Italy', flag: 'it', subtitle: 'Europe' },
  { name: 'Turkey', flag: 'tr', subtitle: 'Europe/Asia' },
];

// Animation variants
const heroVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.1, ease: [0.23, 1.01, 0.32, 1] } },
};

const gridVariants = {
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.2 } },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.85, y: 30 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.55, type: 'spring', stiffness: 80 } },
};

const HeroSection = () => (
  <Box
    component={motion.section}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.7 }}
    variants={heroVariants}
    sx={{
      minHeight: { xs: '60vh', md: '100vh' },
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      background: `url('https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Equirectangular_projection_SW.jpg/1920px-Equirectangular_projection_SW.jpg') center/cover no-repeat`,
      '&:before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        bgcolor: 'rgba(0,0,0,0.45)',
        zIndex: 1,
      },
      px: { xs: 1, md: 0 },
    }}
  >
    <Container
      sx={{
        position: 'relative',
        zIndex: 2,
        textAlign: 'center',
        color: '#fff',
        px: { xs: 0.5, md: 0 },
      }}
      maxWidth="sm"
    >
      <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: '0.08em', textShadow: '0 4px 24px rgba(0,0,0,0.18)', fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
        Export Countries
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 400, mb: 0, textShadow: '0 2px 8px rgba(0,0,0,0.18)', fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>
        FSC-certified timber delivered to 10+ countries
      </Typography>
    </Container>
  </Box>
);

const CountryGrid = () => (
  <Container sx={{ py: { xs: 4, md: 10 }, px: { xs: 0.5, md: 2 } }}>
    <Grid
      container
      spacing={{ xs: 2, md: 4 }}
      justifyContent="center"
      component={motion.div}
      variants={gridVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      {countries.map((country, idx) => (
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          key={country.name}
          component={motion.div}
          variants={cardVariants}
        >
          <Paper elevation={2} sx={{
            p: { xs: 2, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 4,
            minHeight: { xs: 120, md: 160 },
            bgcolor: '#fff',
            boxShadow: '0 4px 24px 0 rgba(166,124,82,0.08)',
            transition: 'box-shadow 0.3s',
            '&:hover': { boxShadow: '0 8px 32px 0 rgba(166,124,82,0.18)', transform: 'scale(1.035)' },
            width: '100%',
            maxWidth: { xs: 340, sm: 'none' },
            mx: 'auto',
          }}>
            <img
              src={`https://flagcdn.com/48x36/${country.flag}.png`}
              alt={`${country.name} flag`}
              style={{ width: 40, height: 30, marginBottom: 12, borderRadius: 4, boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)' }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main', letterSpacing: '0.04em', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
              {country.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.98rem', md: '1rem' } }}>
              {country.subtitle}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Container>
);

const CountriesPage = () => {
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);
  return (
    <Box>
      <HeroSection />
      <CountryGrid />
    </Box>
  );
};

export default CountriesPage; 